import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { nationalIdText } from '@/lib/national-id';
import { PatientContactsList, type PatientContact } from './PatientContacts';
import { PatientFamilyList } from './PatientFamily';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PatientAvatar } from '@/components/PatientAvatar';
import { chosenPatientTab, patientTabIds, withPatientTab } from './patient-tabs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Printer, UserPen } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { PatientInsuranceTab } from '../insurance/PatientInsuranceTab';
import { PatientPackagesCard } from '../billing/PatientPackagesCard';
import { dateOnly, dateTime, humanize, money } from '@/lib/utils';
import { DataTable, Dialog, PageHeader, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, PageSkeleton, Input, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { printDocument } from '@/lib/print';
import { PreviousVisits } from './PreviousVisits';

interface PatientDetail {
  id: string;
  mrn: string;
  fullName: string;
  sex: string;
  ageLabel: string;
  dob: string | null;
  birthWeightGrams?: number | null;
  gestationalAgeWeeks?: number | null;
  gestationalAgeDays?: number | null;
  phone: string | null;
  cnic: string | null;
  bloodGroup: string;
  category: string;
  address: string | null;
  city: string | null;
  guardianName: string | null;
  guardianPhone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  createdAt: string;
  allergies: { id: string; substance: string; reaction: string | null; severity: string }[];
  consents: { id: string; type: string; signedByName: string; signedAt: string }[];
  documents: { id: string; title: string; kind: string; createdAt: string }[];
  coverages: { id: string; policyNo: string; payer: { name: string }; validTo: string | null }[];
  contacts?: PatientContact[];
  isDeceased?: boolean;
  deceasedAt?: string | null;
  photoUrl?: string | null;
}

interface TimelineEvent {
  at: string;
  type: string;
  id: string;
  title: string;
  detail: string;
  by: string | null;
  context: string;
}

/** The full patient record: identity, alerts, and the clinical timeline (SRS 6.1, 6.3). */
export function PatientDetailPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const { can, hasModule } = useAuth();
  const navigate = useNavigate();

  // A permission is not a licence. A Super Admin holds every permission there
  // is, so gating on permission alone offered a pharmacy-only store an
  // appointment button and a clinical timeline it has no modules for — the
  // button led to a page its own sidebar does not carry.
  const clinical = hasModule('opd') || hasModule('ipd') || hasModule('er');
  const toast = useToast();
  const queryClient = useQueryClient();
  // Open on the first tab this facility actually has, or the page would land on
  // a hidden tab and show an empty panel.
  const defaultTab = clinical ? 'timeline' : hasModule('billing') ? 'billing' : 'documents';
  const tabIds = patientTabIds(clinical, hasModule('billing'), hasModule('insurance') && can('coverage.verify', 'patient.read'));
  const [params, setParams] = useSearchParams();
  const tab = chosenPatientTab(params.get('tab'), tabIds, defaultTab);
  const setTab = (id: string) => setParams((current) => withPatientTab(current, id, defaultTab), { replace: true });
  const [reverting, setReverting] = useState(false);

  const patient = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => get<PatientDetail>(`/patients/${patientId}`),
    enabled: Boolean(patientId),
  });

  const timeline = useQuery({
    queryKey: ['patient-timeline', patientId],
    queryFn: () => get<TimelineEvent[]>(`/patients/${patientId}/timeline`),
    enabled: Boolean(patientId) && can('encounter.read', 'encounter.read.own'),
  });

  const invoices = useQuery({
    queryKey: ['patient-invoices', patientId],
    queryFn: () => get<{ data: { id: string; number: string; context: string; status: string; totals: { netMinor: number; balanceMinor: number }; createdAt: string }[] }>(
      `/invoices?patientId=${patientId}&limit=50`,
    ),
    enabled: Boolean(patientId) && can('invoice.read'),
  });

  const printCard = useMutation({
    mutationFn: () => get<{ html: string }>(`/patients/${patientId}/card`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('opd', 'could_not_print_the_card'), describeError(error)),
  });

  if (patient.isLoading) {
    return (
      <PageSkeleton />
    );
  }
  if (!patient.data) return <Alert variant="destructive">{tr('opd', 'that_patient_record_could_not_be_opened')}</Alert>;

  const p = patient.data;

  return (
    <>
      <PageHeader
        title={p.fullName}
        description={tr('opd', 'x_x_x_registered_x', { mrn: p.mrn, ageLabel: p.ageLabel, sex: humanize(p.sex), createdAt: dateOnly(p.createdAt) })}
        actions={
          <>
            {can('patient.update') && !p.isDeceased && (
              <Button variant="outline" onClick={() => navigate(`/patients/${p.id}/edit`)}>
                <UserPen className="h-4 w-4" /> {tr('opd', 'edit_details')}
              </Button>
            )}
            <Button variant="outline" onClick={() => printCard.mutate()} loading={printCard.isPending}>
              <Printer className="h-4 w-4" /> {tr('opd', 'patient_card')}
            </Button>
            {hasModule('opd') && can('appointment.manage') && (
              <Link to="/appointments">
                <Button>{tr('opd', 'book_appointment')}</Button>
              </Link>
            )}
          </>
        }
      />

      {p.isDeceased && (
        <Alert variant="destructive" className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">
              {tr('opd', 'recorded_as_deceased')}{p.deceasedAt ? ` on ${dateTime(p.deceasedAt)}` : ''}{tr('opd', 'the_record_is_read_only')}
            </span>
            {can('patient.deceased.revert') && (
              <Button size="sm" variant="destructive" onClick={() => setReverting(true)}>
                {tr('opd', 'undo_patient_is_alive')}
              </Button>
            )}
          </div>
        </Alert>
      )}

      {p.allergies.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <span className="inline-flex items-center gap-1 font-semibold">
            <AlertTriangle className="h-4 w-4" /> {tr('opd', 'allergies')}
          </span>{' '}
          {p.allergies.map((a) => `${a.substance}${a.reaction ? ` (${a.reaction})` : ''}`).join(', ')}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-4">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-sm">{tr('opd', 'details')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {p.photoUrl && (
              <PatientAvatar
                name={p.fullName}
                photoKey={p.photoUrl}
                className="mx-auto mb-2 grid aspect-square w-full max-w-[9rem] place-items-center rounded-2xl border bg-muted text-2xl font-semibold text-muted-foreground"
              />
            )}
            <Detail label={tr('opd', 'category')} value={<Badge variant="secondary">{p.category}</Badge>} />
            <Detail label={tr('opd', 'blood_group')} value={formatBloodGroup(p.bloodGroup)} />
            <Detail label={tr('opd', 'date_of_birth')} value={dateOnly(p.dob)} />
            {typeof p.birthWeightGrams === 'number' && (
              <Detail label={tr('opd', 'birth_weight')} value={tr('opd', 'birth_weight_value', { grams: p.birthWeightGrams })} />
            )}
            {typeof p.gestationalAgeWeeks === 'number' && (
              <Detail
                label={tr('opd', 'gestational_age')}
                value={tr('opd', 'gestation_value', { weeks: p.gestationalAgeWeeks, days: p.gestationalAgeDays ?? 0 })}
              />
            )}
            <Detail label={tr('opd', 'phone')} value={p.phone ?? '—'} />
            <Detail label={nationalIdText().label} value={p.cnic ?? '—'} />
            <Detail label={tr('opd', 'address')} value={[p.address, p.city].filter(Boolean).join(', ') || '—'} />
            <div className="pt-1">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{tr('opd', 'guardians_and_contacts')}</p>
              <PatientContactsList patientId={p.id} contacts={p.contacts ?? []} canEdit={can('patient.update', 'patient.register')} />
            </div>
            <div className="pt-1">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{tr('opd', 'family')}</p>
              <PatientFamilyList patientId={p.id} patientName={p.fullName} canEdit={can('patient.update')} />
            </div>
            {p.coverages.length > 0 && (
              <Detail
                label={tr('opd', 'coverage')}
                value={p.coverages.map((c) => `${c.payer.name} · ${c.policyNo}`).join(', ')}
              />
            )}
            {can('patient.update', 'encounter.write') && (
              <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => navigate(`/patients/${p.id}/allergies/new`)}>
                {tr('opd', 'record_allergy')}
              </Button>
            )}
          </CardContent>
        </Card>

        <div className="lg:col-span-3">
          <Tabs
            active={tab}
            onChange={setTab}
            tabs={[
              // Without a clinical module there are no encounters to time-line,
              // so the tab could only ever say "no history yet".
              ...(clinical ? [{ id: 'timeline', label: tr('opd', 'clinical_timeline') }] : []),
              ...(clinical ? [{ id: 'visits', label: tr('opd', 'previous_visits') }] : []),
              ...(hasModule('billing') ? [{ id: 'billing', label: tr('opd', 'billing') }] : []),
              ...(hasModule('insurance') && can('coverage.verify', 'patient.read') ? [{ id: 'insurance', label: tr('opd', 'insurance') }] : []),
              { id: 'documents', label: tr('opd', 'documents_and_consents') },
            ]}
          />

          {tab === 'timeline' &&
            (can('encounter.read', 'encounter.read.own') ? (
              timeline.isLoading ? (
                <PageSkeleton />
              ) : timeline.data && timeline.data.length > 0 ? (
                <ol className="space-y-2">
                  {timeline.data.map((event) => (
                    <li key={`${event.type}-${event.id}`} className="rounded-lg border bg-card p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium">{event.title}</p>
                          {event.detail && <p className="text-sm text-muted-foreground">{event.detail}</p>}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="muted">{event.type}</Badge>
                          <StatusBadge status={event.context} />
                          <span>{dateTime(event.at)}</span>
                        </div>
                      </div>
                      {event.by && <p className="mt-1 text-xs text-muted-foreground">{tr('opd', 'by')} {event.by}</p>}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
                  {tr('opd', 'no_clinical_history_yet')}
                </p>
              )
            ) : (
              <Alert>{tr('opd', 'your_role_does_not_include_access_to_clinical')}</Alert>
            ))}

          {tab === 'visits' &&
            (can('encounter.read', 'encounter.read.own') ? (
              <PreviousVisits patientId={p.id} />
            ) : (
              <Alert>{tr('opd', 'your_role_does_not_include_access_to_clinical')}</Alert>
            ))}

          {tab === 'billing' &&
            (can('invoice.read') ? (
              <>
              <div className="mb-3">
                <PatientPackagesCard patientId={p.id} sellKind="session_bundle" />
              </div>
              <div className="mb-3 flex justify-end">
                <Link to={`/billing/statement?patientId=${p.id}`}>
                  <Button variant="outline">{tr('opd', 'statement_of_account')}</Button>
                </Link>
              </div>
              <DataTable
                rows={invoices.data?.data}
                loading={invoices.isLoading} refreshing={invoices.isFetching}
                error={invoices.error}
                rowKey={(row) => row.id}
                empty={tr('opd', 'no_invoices_for_this_patient')}
                columns={[
                  { header: tr('opd', 'invoice'), sort: (row) => row.number, cell: (row) => <Link className="underline" to={`/billing/invoices/${row.id}`}>{row.number}</Link> },
                  { header: tr('opd', 'context'), sort: (row) => row.context, cell: (row) => humanize(row.context) },
                  { header: tr('opd', 'date'), sort: (row) => row.createdAt, cell: (row) => dateOnly(row.createdAt) },
                  { header: tr('opd', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
                  { header: tr('opd', 'total'), align: 'right', sort: (row) => row.totals.netMinor, cell: (row) => money(row.totals.netMinor) },
                  {
                    header: tr('opd', 'balance'),
                    align: 'right',
                    sort: (row) => row.totals.balanceMinor,
                    cell: (row) => (
                      <span className={row.totals.balanceMinor > 0 ? 'font-semibold text-destructive' : ''}>
                        {money(row.totals.balanceMinor)}
                      </span>
                    ),
                  },
                ]}
              />
              </>
            ) : (
              <Alert>{tr('opd', 'your_role_does_not_include_billing_access')}</Alert>
            ))}

          {tab === 'insurance' && hasModule('insurance') && patientId && <PatientInsuranceTab patientId={patientId} />}

          {tab === 'documents' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{tr('opd', 'consents')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {p.consents.length === 0 && <p className="text-muted-foreground">{tr('opd', 'no_consents_recorded')}</p>}
                  {p.consents.map((consent) => (
                    <div key={consent.id} className="flex justify-between border-b pb-1 last:border-0">
                      <span>{humanize(consent.type)}</span>
                      <span className="text-muted-foreground">{dateOnly(consent.signedAt)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{tr('opd', 'documents')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {p.documents.length === 0 && <p className="text-muted-foreground">{tr('opd', 'no_documents_uploaded')}</p>}
                  {p.documents.map((document) => (
                    <div key={document.id} className="flex justify-between border-b pb-1 last:border-0">
                      <span>{document.title}</span>
                      <span className="text-muted-foreground">{dateOnly(document.createdAt)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {reverting && (
        <RevertDeceasedDialog
          patientId={p.id}
          patientName={p.fullName}
          onClose={() => setReverting(false)}
          onReverted={() => {
            setReverting(false);
            void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
          }}
        />
      )}
    </>
  );
}

interface RevertDeceasedResult {
  patientId: string;
  reopened: boolean;
  recordKept: boolean;
  certificatesVoided: number;
}

function RevertDeceasedDialog({
  patientId,
  patientName,
  onClose,
  onReverted,
}: {
  patientId: string;
  patientName: string;
  onClose: () => void;
  onReverted: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const revert = useMutation({
    mutationFn: () => post<RevertDeceasedResult>(`/mortality/patients/${patientId}/revert`, { reason: reason.trim() }),
    onSuccess: (result) => {
      const details = [
        result.certificatesVoided > 0
          ? `${result.certificatesVoided} death certificate${result.certificatesVoided === 1 ? '' : 's'} voided`
          : null,
        result.recordKept ? 'The death stays on the emergency case as an unidentified death' : null,
      ].filter(Boolean);
      toast.success(tr('opd', 'the_patient_record_is_open_again'), details.length > 0 ? details.join(' · ') : undefined);
      onReverted();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('opd', 'patient_is_alive')}
      description={tr('opd', 'x_was_recorded_as_deceased_by_mistake', { patientName: patientName })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('opd', 'cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              revert.mutate();
            }}
            loading={revert.isPending}
            disabled={reason.trim().length < 5}
          >
            {tr('opd', 'undo_the_death_record')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning">
          {tr('opd', 'this_reopens_the_patient_record_and_voids_any')}
        </Alert>
        <Field label={tr('opd', 'reason')} required hint={tr('opd', 'at_least_5_characters_what_was_wrong_with_the')}>
          <Input maxLength={400} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="text-end">{value}</span>
    </div>
  );
}

function formatBloodGroup(value: string): string {
  const map: Record<string, string> = {
    a_pos: 'A+', a_neg: 'A−', b_pos: 'B+', b_neg: 'B−',
    ab_pos: 'AB+', ab_neg: 'AB−', o_pos: 'O+', o_neg: 'O−', unknown: '—',
  };
  return map[value] ?? '—';
}
